/**
 * The library's recipe model — the same one `LibraryContent.swift` describes.
 *
 * A sound is a *recipe*, never a hand-written buffer: one to three `SynthPatch` layers mixed
 * at their own gains, offsets and pans, then an optional offline effect chain, then one
 * shared finishing stage. Layering is not decoration — it is how a real kit is built. A kick
 * is a beater click on top of a tuned body; a snare is a noise burst on top of a tuned shell.
 * One patch cannot give two halves of a drum two different envelopes, and a drum whose click
 * and body decay together sounds synthetic in the first millisecond.
 */

import { emptyEffectChain, type EffectChain, type PadColor } from '../../model/types';
import type { SynthPatch } from '../synth';

// MARK: - Category

/**
 * The family a library sound belongs to. Drives the pad colour, the tag it is filed under,
 * and — importantly — how its level is set.
 */
export type LibraryCategory = 'kick' | 'snare' | 'hat' | 'clap' | 'percussion' | '808' | 'bass';

export const LIBRARY_CATEGORIES: readonly LibraryCategory[] = [
  'kick',
  'snare',
  'hat',
  'clap',
  'percussion',
  '808',
  'bass',
];

export function libraryCategoryDisplayName(category: LibraryCategory): string {
  switch (category) {
    case 'kick':
      return 'Kicks';
    case 'snare':
      return 'Snares';
    case 'hat':
      return 'Hi-Hats';
    case 'clap':
      return 'Claps';
    case 'percussion':
      return 'Percussion';
    case '808':
      return '808s';
    case 'bass':
      return 'Bass';
  }
}

/** SF Symbol name, shared with the native build; the web icon layer maps it to an SVG. */
export function libraryCategorySymbolName(category: LibraryCategory): string {
  switch (category) {
    case 'kick':
      return 'circle.fill';
    case 'snare':
      return 'square.grid.2x2.fill';
    case 'hat':
      return 'aqi.medium';
    case 'clap':
      return 'hands.clap.fill';
    case 'percussion':
      return 'cylinder.fill';
    case '808':
      return 'waveform.path';
    case 'bass':
      return 'speaker.wave.3.fill';
  }
}

export function libraryCategoryPadColor(category: LibraryCategory): PadColor {
  switch (category) {
    case 'kick':
      return 'magenta';
    case 'snare':
      return 'coral';
    case 'hat':
      return 'cyan';
    case 'clap':
      return 'amber';
    case 'percussion':
      return 'violet';
    case '808':
      return 'indigo';
    case 'bass':
      return 'mint';
  }
}

/**
 * Pads in the same family cut each other off, the way one hi-hat pedal can only be in one
 * state at a time and one 808 slides into the next rather than stacking into mud.
 */
export function libraryCategoryChokeGroup(category: LibraryCategory): number | null {
  switch (category) {
    case 'hat':
      return 1;
    case '808':
      return 2;
    case 'kick':
    case 'snare':
    case 'clap':
    case 'percussion':
    case 'bass':
      return null;
  }
}

export interface LibraryLevelPolicy {
  /** Match the active-region K-weighted level to `targetDB`. */
  kind: 'loudness' | 'peak';
  targetDB: number;
}

/**
 * Level the finished render is matched to.
 *
 * Everything is matched by *loudness*, not by peak. Peak-matching a kit leaves a noise hat
 * far quieter than a sine kick at the same peak, which is the single fastest way to make a
 * drum kit unusable. The numbers are not arbitrary either: each one is close to what that
 * family already measures at, so the correction is a couple of dB rather than a boost big
 * enough to need heavy clipping to survive. What the spread between them encodes is an
 * ordinary kit balance — drums on top, percussion just under them, claps and the low end
 * sitting below the backbeat rather than fighting it.
 */
export function libraryCategoryLevelPolicy(category: LibraryCategory): LibraryLevelPolicy {
  switch (category) {
    case 'kick':
    case 'snare':
      return { kind: 'loudness', targetDB: -8.0 };
    case 'hat':
    case 'percussion':
      return { kind: 'loudness', targetDB: -8.5 };
    case 'clap':
      return { kind: 'loudness', targetDB: -11.0 };
    // The chromatic set is levelled by loudness like everything else, which means the bottom
    // notes end up with more amplitude than the top ones. That is the point: a C1 and a C3 at
    // the same *peak* do not sound like the same instrument played two octaves apart, because
    // neither the ear nor a phone speaker has much to say about 33 Hz. Matching loudness is
    // what makes the set playable as a bass line.
    case '808':
      return { kind: 'loudness', targetDB: -9.5 };
    case 'bass':
      return { kind: 'loudness', targetDB: -12.0 };
  }
}

// MARK: - Recipe

/** One `SynthPatch` placed into a finished sound. */
export interface LibraryLayer {
  patch: SynthPatch;
  /**
   * Level relative to the layer's own peak-normalised render, in dB. Every patch leaves the
   * synth renderer at −1 dBFS, so this number is the whole balance between layers and means
   * the same thing in every recipe.
   */
  gainDB: number;
  /**
   * Offset from the start of the sound. A few milliseconds of it is what turns two stacked
   * hits into one drum with a front and a back.
   */
  delay: number;
  pan: number;
}

export interface LibraryLayerOptions {
  gainDB?: number;
  delay?: number;
  pan?: number;
}

export function libraryLayer(patch: SynthPatch, options: LibraryLayerOptions = {}): LibraryLayer {
  return {
    patch,
    gainDB: options.gainDB ?? 0,
    delay: options.delay ?? 0,
    pan: options.pan ?? 0,
  };
}

/** A complete library sound: what it is called, how it is filed, and how to make it. */
export interface LibrarySound {
  /**
   * Stable slug. Also the file stem, the seed source, and the identifier a pack entry is
   * keyed by, so a regenerated library lines up with the one it replaced.
   */
  id: string;
  name: string;
  category: LibraryCategory;
  tags: string[];
  /** Spoken by the screen reader in place of the file name. A sentence, not a label. */
  caption: string;
  layers: LibraryLayer[];
  /** Applied to the mixed layers, before levelling. */
  post: EffectChain;
}

export interface LibrarySoundOptions {
  tags?: string[];
  post?: EffectChain;
}

export function librarySound(
  id: string,
  name: string,
  category: LibraryCategory,
  caption: string,
  layers: LibraryLayer[],
  options: LibrarySoundOptions = {},
): LibrarySound {
  return {
    id,
    name,
    category,
    tags: options.tags ?? [],
    caption,
    layers,
    post: options.post ?? emptyEffectChain(),
  };
}

export function librarySoundFileName(sound: LibrarySound): string {
  return `${sound.id}.wav`;
}

/** Category first, so filtering the library by family is a tag search like any other. */
export function librarySoundTags(sound: LibrarySound): string[] {
  const result: string[] = [sound.category];
  for (const tag of sound.tags) if (!result.includes(tag)) result.push(tag);
  return result;
}
